import { config } from "@/config";
import Sentry from "@/integrations/Sentry";
import { queryClient } from "@/queryClient";
import { Auth0ContextInterface, useAuth0 } from "@auth0/auth0-react";
import axios from "axios";
import { useEffect } from "react";
import { handleAxiosError } from "./utils";

import { wait } from "@/utils/wait";
import { CohesiveAPIClient, MeData } from "@cohesive/api-client";
import { auth0Config } from "../auth0.config";
import { getCanvasCaptureBearerToken } from "./canvasCaptureAuth";

/** `canvas-capture.html` entry: no Auth0; orgAPI uses capture access JWT from inject or URL. */
export function CaptureOnlyApiInject() {
  useEffect(() => {
    orgAPI.setGetToken(async () => {
      const capture = getCanvasCaptureBearerToken();
      if (!capture) {
        throw new Error("Canvas capture token not set");
      }
      return capture;
    });
    authInterceptor.setAuthGetter(null);
  }, []);

  return null;
}

export const api = axios.create({
  baseURL: config.hosts.api,
});

class AuthInterceptor {
  getToken: Auth0ContextInterface["getAccessTokenSilently"] | null = null;

  setAuthGetter(
    getToken: Auth0ContextInterface["getAccessTokenSilently"] | null,
  ) {
    this.getToken = getToken;
  }

  constructor() {
    this.intercept = this.intercept.bind(this);
    this.setAuthGetter = this.setAuthGetter.bind(this);
  }

  async intercept(config: any) {
    if (!config.headers.Authorization) {
      const captureToken = getCanvasCaptureBearerToken();
      if (captureToken) {
        config.headers["Authorization"] = `Bearer ${captureToken}`;
        return config;
      }

      let token = null;

      while (!token) {
        try {
          await wait(50);
          token = await this.getToken?.();
        } catch (e) {
          console.error("failed to get token", e);
        }
      }

      config.headers["Authorization"] = `Bearer ${token}`;
    }
    return config;
  }
}

export function AuthInject() {
  const { getAccessTokenSilently, isLoading } = useAuth0();

  useEffect(() => {
    if (!isLoading) {
      authInterceptor.setAuthGetter(getAccessTokenSilently);
    }

    orgAPI.setGetToken(getAccessTokenSilently);

    return () => authInterceptor.setAuthGetter(null);
  }, [getAccessTokenSilently, isLoading]);

  return null;
}

export const authInterceptor = new AuthInterceptor();

export async function getToken() {
  return authInterceptor.getToken?.();
}

// Add auth interceptor to axios instance
api.interceptors.request.use(authInterceptor.intercept);

/**
 * Shared response error handler for both axios instances. A 402 from the
 * backend's usage gate when our cached MeData still says usage is allowed
 * means another user in the org just pushed it over the overage limit —
 * the cache is stale. Refresh /me so the gate UI catches up, and skip
 * Sentry: that race is expected. Other 402s (cache already says
 * disallowed, or no cached MeData) fall through to Sentry — they indicate
 * a request slipped past UI gating, which is worth capturing.
 */
function handleResponseError(error: unknown) {
  if (axios.isAxiosError(error) && error.response?.status === 402) {
    const userInfo = queryClient.getQueryData<MeData>(["userInfo"]);
    if (userInfo?.usage?.usage_allowed === true) {
      queryClient.invalidateQueries({ queryKey: ["userInfo"] });
      return Promise.reject(handleAxiosError(error));
    }
  }
  Sentry.captureException(error);
  return Promise.reject(handleAxiosError(error));
}

// Add error handling interceptor
api.interceptors.response.use((response) => response, handleResponseError);

// Create the organization API client (no @auth0/auth0-spa-js instance — avoids secure-origin
// failures on hosts like host.docker.internal; Auth0 React supplies tokens via setGetToken).
export const orgAPI = new CohesiveAPIClient({
  axiosOptions: {
    baseURL: config.hosts.api,
  },
  auth0Options: auth0Config,
  useAuth0Spa: false,
});

orgAPI.axios.interceptors.request.use(authInterceptor.intercept);
orgAPI.axios.interceptors.response.use(
  (response) => response,
  handleResponseError,
);
